/**
 * Broadcast Create Dialog
 * Create channel → show read URL, post token, QR for read URL
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { WindowFrame } from '../aim-ui/WindowFrame';
import { Button98 } from '../aim-ui/Button98';
import { Field, FieldLabel, FullInput, ButtonBar, HelpText } from '../aim-ui/FormField';
import { aimTheme } from '../../theme/aim-theme';
import apiClient from '../../utils/api-client';
import { endpoints } from '../../config/api-endpoints';
import { getAccessToken } from '../../config/cognito-config';
import { generateBroadcastKey } from '../../utils/broadcast-e2ee';
import { saveBroadcastKey } from '../../utils/broadcast-key-store';
import type { CreateBroadcastChannelResult } from '../../types';

interface BroadcastCreateDialogProps {
  onSave: (result: CreateBroadcastChannelResult) => void;
  onClose: () => void;
}

const DialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: ${aimTheme.colors.gray};
  padding: ${aimTheme.spacing.md};
`;

const QRContainer = styled.div`
  background: ${aimTheme.colors.white};
  padding: ${aimTheme.spacing.lg};
  box-shadow: var(--border-field);
  border: none;
  margin: ${aimTheme.spacing.md} 0;
  align-self: center;
`;

const UrlBlock = styled.div`
  font-size: ${aimTheme.fonts.size.small};
  word-break: break-all;
  padding: ${aimTheme.spacing.sm};
  background: ${aimTheme.colors.white};
  box-shadow: var(--border-field);
  border: none;
  margin: ${aimTheme.spacing.sm} 0;
`;

export const BroadcastCreateDialog: React.FC<BroadcastCreateDialogProps> = ({
  onSave,
  onClose,
}) => {
  const [displayName, setDisplayName] = useState('');
  const [allowGuestPosts, setAllowGuestPosts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CreateBroadcastChannelResult | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) return;
    setLoading(true);
    try {
      // Generate E2EE key client-side before API call
      const encryptionKey = await generateBroadcastKey();
      const token = await getAccessToken();
      const response = await apiClient.post(
        endpoints.public.broadcastCreate(),
        { display_name: displayName.trim(), allow_guest_posts: allowGuestPosts },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const raw = response.data;
      if (typeof raw === 'string' && raw.trimStart().startsWith('<!')) {
        console.error('API returned HTML instead of JSON — request likely hit the SPA origin.', raw.slice(0, 200));
        throw new Error('API_UNREACHABLE');
      }
      const data = (raw?.data ?? raw) as CreateBroadcastChannelResult | undefined;
      if (!data?.read_url) {
        console.error('Unexpected API response:', raw);
        throw new Error('Invalid response from server');
      }
      // Attach encryption key and modify read_url to include fragment
      const resultWithKey: CreateBroadcastChannelResult = {
        ...data,
        encryption_key: encryptionKey,
        read_url: `${data.read_url}#${encryptionKey}`,
      };
      // Persist key immediately so it survives even if dialog is closed with X
      saveBroadcastKey(data.channel_id, encryptionKey, data.post_token).catch(() => {});
      try {
        const stored = JSON.parse(localStorage.getItem('bw:bc:encKeys') || '{}');
        stored[data.channel_id] = encryptionKey;
        localStorage.setItem('bw:bc:encKeys', JSON.stringify(stored));
      } catch { /* ignore */ }
      setResult(resultWithKey);
      // Auto-copy read link so owner can share in one less click
      const readUrlWithKey = `${data.read_url}#${encryptionKey}`;
      navigator.clipboard.writeText(readUrlWithKey).catch(() => {});
      toast.success('Read link copied to clipboard — share it so people can view the feed');
    } catch (error) {
      console.error('Failed to create broadcast channel:', error);
      const isApiUnreachable =
        (error as { code?: string; message?: string })?.code === 'API_UNREACHABLE' ||
        (error instanceof Error && (error.message === 'API_UNREACHABLE' || error.message.includes('API returned HTML')));
      const message = isApiUnreachable
        ? 'API unreachable. On dev, ensure CloudFront routes /api/* to your backend (redeploy Frontend stack with ALB).'
        : 'Failed to create channel. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.read_url);
    toast.success('Read link copied!');
  };

  const handleCopyToken = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.post_token);
    toast.success('Post token copied! Save it — it won’t be shown again.');
  };

  const handleDone = () => {
    if (result) onSave(result);
    onClose();
  };

  if (result) {
    return (
      <WindowFrame
        title="Broadcast channel created"
        width={440}
        height={580}
        initialX={160}
        initialY={60}
        zIndex={1002}
        onClose={onClose}
      >
        <DialogContainer>
          <Field>
            <FieldLabel>Channel: {result.display_name}</FieldLabel>
            <FieldLabel style={{ fontWeight: 'normal', fontSize: aimTheme.fonts.size.small }}>
              Read URL (share this so people can view the feed):
            </FieldLabel>
            <UrlBlock>{result.read_url}</UrlBlock>
          </Field>
          <Field>
            <FieldLabel style={{ fontSize: aimTheme.fonts.size.small }}>
              Post token (keep secret — use to add posts or burn the channel):
            </FieldLabel>
            <UrlBlock style={{ wordBreak: 'break-all' }}>{result.post_token}</UrlBlock>
          </Field>
          <QRContainer>
            <QRCodeSVG value={result.read_url} size={200} level="H" />
          </QRContainer>
          <ButtonBar>
            <Button98 onClick={handleCopyLink}>Copy read link</Button98>
            <Button98 onClick={handleCopyToken}>Copy post token</Button98>
            <Button98 onClick={handleDone}>Done</Button98>
          </ButtonBar>
        </DialogContainer>
      </WindowFrame>
    );
  }

  return (
    <WindowFrame
      title="New broadcast channel"
      width={400}
      height={360}
      initialX={180}
      initialY={120}
      zIndex={1002}
      onClose={onClose}
    >
      <DialogContainer>
        <form onSubmit={handleSubmit}>
          <Field>
            <FieldLabel>Channel name</FieldLabel>
            <FullInput
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Community Alerts"
              maxLength={100}
              autoFocus
            />
          </Field>
          <Field>
            <div className="field-row">
              <input
                id="allow-guest-posts"
                type="checkbox"
                checked={allowGuestPosts}
                onChange={(e) => setAllowGuestPosts(e.target.checked)}
              />
              <label htmlFor="allow-guest-posts">Allow anyone with the link to post</label>
            </div>
            <HelpText>Guests with the read URL can add posts. Posts are still end-to-end encrypted.</HelpText>
          </Field>
          <ButtonBar>
            <Button98 type="submit" disabled={loading || !displayName.trim()}>
              {loading ? 'Creating…' : 'Create channel'}
            </Button98>
            <Button98 type="button" onClick={onClose}>
              Cancel
            </Button98>
          </ButtonBar>
        </form>
      </DialogContainer>
    </WindowFrame>
  );
};
